import React from 'react';

import { NotificationsContext } from '.';
import { ReservationStatus, UserAccessRequestResultValue } from '../../graphql/API';
import useAccessRequestQuery from '../../hooks/reactQuery/useAccessRequestQuery';
import { useListInvitesV2Query } from '../../hooks/reactQuery/useListInvitesV2Query';
import useListReservationContentQuery from '../../hooks/reactQuery/useListReservationContentQuery';
import useReservationQuery from '../../hooks/reactQuery/useReservationQuery';
import { usePersistLocalStorageState } from '../../hooks/usePersistLocalStorageState';
import useVenueId from '../../hooks/useVenueId';

export const notificationStatsName = {
  lastReservationCreatedAtTime: 'lastReservationCreatedAtTime',
  lastCollabUpdatedAtTime: 'lastCollabUpdatedAtTime',
  lastContentUpdatedAtTime: 'lastContentUpdatedAtTime',
  lastAccessRequestUpdatedAtTime: 'lastAccessRequestUpdatedAtTime',
};

const NotificationsProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const venueID = useVenueId() || '';
  const lastLoggedInVenueID = localStorage.getItem('lastLoggedInVenueID');

  const [lastReservationCreatedAtTime, setLastReservationCreatedAtTime] =
    usePersistLocalStorageState(notificationStatsName.lastReservationCreatedAtTime, '');
  const [lastCollabUpdatedAtTime, setLastCollabUpdatedAtTime] = usePersistLocalStorageState(
    notificationStatsName.lastCollabUpdatedAtTime,
    '',
  );
  const [lastContentUpdatedAtTime, setLastContentUpdatedAtTime] = usePersistLocalStorageState(
    notificationStatsName.lastContentUpdatedAtTime,
    '',
  );
  const [lastAccessRequestUpdatedAtTime, setLastAccessRequestContentUpdatedAtTime] =
    usePersistLocalStorageState(notificationStatsName.lastAccessRequestUpdatedAtTime, '');

  const { data: reservations, refetch } = useReservationQuery(
    { status: [ReservationStatus.reserved] },
    venueID,
    1,
  );
  const { data: invites } = useListInvitesV2Query(
    venueID || '',
    {
      sortBy: 'invites.updatedAt',
      sortDirection: 'desc',
    },
    1,
  );

  const { data: contents } = useListReservationContentQuery({ byVenue: false }, 1);
  const { data: accessRequest } = useAccessRequestQuery(
    {
      status: UserAccessRequestResultValue.requested,
      sortBy: 'createdAt',
      sortDirection: 'desc',
    },
    false,
    1,
  );

  React.useEffect(() => {
    if (String(lastLoggedInVenueID) !== String(venueID)) {
      setLastCollabUpdatedAtTime('');
    }
  }, [lastLoggedInVenueID, venueID]);

  const handleUpdateInvite = (val: string) => {
    setLastCollabUpdatedAtTime(val);
  };

  const handleUpdateReservation = (val: string) => {
    setLastReservationCreatedAtTime(val);
  };

  const handleUpdateContent = (val: string) => {
    setLastContentUpdatedAtTime(val);
  };

  const handleUpdateAccessRequest = (val: string) => {
    setLastAccessRequestContentUpdatedAtTime(val);
  };

  return (
    <NotificationsContext.Provider
      value={{
        lastCollabUpdatedAtTime,
        lastReservationCreatedAtTime,
        lastContentUpdatedAtTime,
        lastAccessRequestUpdatedAtTime,
        reservation: reservations.length ? reservations[0] : null,
        invite: invites.length ? invites[0] : null,
        content: contents.length ? contents[0] : null,
        accessRequest: accessRequest ? accessRequest[0] : null,
        handleUpdateInvite,
        handleUpdateReservation,
        handleUpdateContent,
        handleUpdateAccessRequest,
        refetchReservationsHandler: async () => {
          await refetch();
          setLastReservationCreatedAtTime(reservations[0]?.createdAt || '');
        },
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};

export default NotificationsProvider;
